import React, { useState, useEffect } from 'react';
import CustomModal from '../Modal/CustomModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faYoutube } from "@fortawesome/free-brands-svg-icons";
import MarkdownEditor from './MarkdownEditor';
import { customSwal } from '../CustomSwal';

const ListItemModal = ({
  isOpen,
  onClose,
  consoleItemId,
  listItem,
  dispatch,
  addListItem,
  updateListItem
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null); // Store file object instead of base64
  const [imagePreview, setImagePreview] = useState(''); // For preview display
  const [link, setLink] = useState('');
  const [contentType, setContentType] = useState('image'); // 'image' or 'link'

  // Also runs on every open: the modal stays mounted, so a cancelled draft would otherwise come back
  useEffect(() => {
    if (listItem) {
      setTitle(listItem.title || '');
      setDescription(listItem.description || '');
      // For existing items, we have a URL, so set it as preview
      setImagePreview(listItem.image || '');
      setImage(null); // No file object for existing images
      setLink(listItem.link || '');
      // Determine content type based on what data exists
      if (listItem.link) {
        setContentType('link');
      } else {
        setContentType('image');
      }
    } else {
      setTitle('');
      setDescription('');
      setImage(null);
      setImagePreview('');
      setLink('');
      setContentType('image');
    }
  }, [listItem, isOpen]);

  const resizeImage = (file, maxWidth = 800, maxHeight = 600, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        customSwal.fire({
          icon: 'error',
          title: 'Invalid file type',
          text: 'Please select an image file',
        });
        return;
      }

      try {
        // Resize the image to reduce size
        const resizedFile = await resizeImage(file);
        
        // Check resized file size (should be much smaller now)
        if (resizedFile.size > 2 * 1024 * 1024) { // 2MB limit after compression
          customSwal.fire({
            icon: 'error',
            title: 'File still too large',
            text: 'Image is too large even after compression. Please select a smaller image.',
          });
          return;
        }

        setImage(resizedFile);
        // Create preview URL
        const previewUrl = URL.createObjectURL(resizedFile);
        setImagePreview(previewUrl);
        
        console.log(`Image compressed from ${(file.size / 1024).toFixed(0)}KB to ${(resizedFile.size / 1024).toFixed(0)}KB`);
      } catch (error) {
        customSwal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to process image',
        });
      }
    }
  };

  const handleContentTypeChange = (type) => {
    setContentType(type);
    // Clear the other field when switching types
    if (type === 'image') {
      setLink('');
    } else {
      setImage(null);
      setImagePreview('');
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      customSwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please fill in title and description',
      });
      return;
    }

    // Validate based on content type
    if (contentType === 'link' && !link.trim()) {
      customSwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please provide a link',
      });
      return;
    }

    try {
      let imageData = '';
      
      // Convert file to base64 only when submitting
      if (image && (image instanceof File || image instanceof Blob)) {
        // Final size check before conversion
        if (image.size > 2 * 1024 * 1024) {
          customSwal.fire({
            icon: 'error',
            title: 'File too large',
            text: 'Image is still too large. Please select a smaller image.',
          });
          return;
        }
        
        const reader = new FileReader();
        const base64Promise = new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(image);
        });
        imageData = await base64Promise;
      } else if (contentType === 'image' && !image && listItem) {
        // Keep the existing image, unless it was removed: send '' so the server clears it and cleans it up
        imageData = imagePreview ? undefined : '';
      }

      if (listItem) {
        // Update existing list item
        dispatch(updateListItem(consoleItemId, listItem._id, title, description, imageData, link));
      } else {
        // Add new list item
        dispatch(addListItem(consoleItemId, title, description, imageData, link));
      }
      
      // Reset form
      setTitle('');
      setDescription('');
      setImage(null);
      setImagePreview('');
      setLink('');
    } catch (error) {
      customSwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to process image. Please try a smaller file.',
      });
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview('');
  };

  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={listItem ? 'Edit List Item' : 'Add List Item'}
      onConfirm={handleSubmit}
      confirmText={listItem ? 'Save Changes' : 'Add Item'}
      size="xl"
    >
      <div className="space-y-4">
        <div>
          <label className="block mb-2 font-medium">Title:</label>
          <input
            type="text"
            className="w-full p-2 rounded bg-gray-700 text-white"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter title"
          />
        </div>

        <div>
          <label className="block mb-2 font-medium">Description (Markdown supported):</label>
          <MarkdownEditor
            value={description}
            onChange={setDescription}
            height={200}
            uploadTarget={{ consoleItemId }}
          />
        </div>

        <div>
          <label className="block mb-2 font-medium">Content Type:</label>
          <div className="flex space-x-4 mb-4">
            <button
              type="button"
              className={`px-4 py-2 rounded flex items-center ${
                contentType === 'image' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
              }`}
              onClick={() => handleContentTypeChange('image')}
            >
              <FontAwesomeIcon icon={faImage} className="mr-2" />
              Image
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded flex items-center ${
                contentType === 'link' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
              }`}
              onClick={() => handleContentTypeChange('link')}
            >
              <FontAwesomeIcon icon={faYoutube} className="mr-2" />
              Link/Video
            </button>
          </div>
        </div>

        {contentType === 'image' ? (
          <div>
            <label className="block mb-2 font-medium">Image:</label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="list-item-image-upload"
                />
                <label
                  htmlFor="list-item-image-upload"
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded cursor-pointer"
                >
                  <FontAwesomeIcon icon={faImage} className="mr-2" />
                  Upload Image
                </label>
              </div>

              {imagePreview && (
                <div className="flex items-center space-x-3 p-2 bg-gray-700 rounded">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-gray-300">
                      {image instanceof File ? `New image: ${image.name}` : 'Current image'}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded"
                    onClick={removeImage}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            <label className="block mb-2 font-medium">Link (YouTube, etc.):</label>
            <input
              type="url"
              className="w-full p-2 rounded bg-gray-700 text-white"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="Enter YouTube URL or other link"
            />

            {link && getYouTubeVideoId(link) && (
              <div className="mt-2 p-2 bg-gray-700 rounded">
                <p className="text-sm text-gray-300 mb-2">YouTube Video Preview:</p>
                <img
                  src={getYouTubeThumbnail(link)}
                  alt="YouTube thumbnail"
                  className="w-32 h-24 object-cover rounded"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default ListItemModal;